import {
  AttachMoney,
  Groups,
  People,
  PersonAdd,
  ShoppingCart,
} from "@mui/icons-material";
import { Box, Stack, Typography, alpha, styled } from "@mui/material";
import Loading from "Components/Loading/Loading";
import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import AnalyticsCardPanelHeader from "features/analytics/components/AnalyticsCardPanelHeader";
import useFormatMetric from "features/analytics/hooks/useFormatMetric";
import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import useChartData from "features/analytics/hooks/useChartData";
import { formatFloat } from "Helpers/numbers";
import { formatPercentage, percentageOf } from "Helpers/percentage";
import { fadedPrimaryBg } from "features/onboarding/woocommerce/WooCommerceOnboardingPlatformLogoBox";
import { useSelectedProject } from "Hooks";
import { useFromToContext } from "Providers/FromToProvider";
import { ReactNode, useMemo } from "react";

const InsightRowCard = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  backgroundColor: alpha(theme.palette.common.white, 0.72),
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    borderColor: alpha(theme.palette.primary.main, 0.18),
    boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.08)}`,
  },
}));

const InsightIcon = styled(Box)(({ theme }) => ({
  width: 36,
  height: 36,
  borderRadius: theme.shape.borderRadius,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.primary.main,
  flexShrink: 0,
  background: fadedPrimaryBg(theme),
}));

const insightsChart = {
  name: "Customer Insights",
  dimension: [ApiDimension.DIMENSION_NOT_SET],
  template: ApiChartTemplate.SNAPSHOT,
  type: ApiChartType.GRAPH,
  attributes: [],
  displayOptions: {
    noTitle: true,
    noAttributionToggle: true,
  },
  yAxisKeys: [
    {
      key: "l",
      fields: [
        ApiMetric.METRIC_NEW_CUSTOMERS_COUNT,
        ApiMetric.METRIC_REPEAT_CUSTOMERS_COUNT,
        ApiMetric.METRIC_CUSTOMERS_COUNT,
        ApiMetric.METRIC_SALES_COUNT,
        ApiMetric.METRIC_REVENUE,
      ],
    },
  ],
};

function InsightRow({
  icon,
  label,
  value,
  subtitle,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  subtitle?: string;
}) {
  return (
    <InsightRowCard>
      <InsightIcon>{icon}</InsightIcon>
      <Stack
        flex={1}
        minWidth={0}
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Typography variant="subtitle2" lineHeight={1.2}>
          {label}
        </Typography>
        <Stack alignItems={"flex-end"}>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2} mt={0.25}>
            {value}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </InsightRowCard>
  );
}

function StoreCustomerInsights() {
  const { selectedProject } = useSelectedProject();
  const { chartOutput } = useFromToContext();
  const formatMetric = useFormatMetric();

  const { chartData, loading } = useChartData(
    selectedProject,
    insightsChart,
    chartOutput,
    { ignoreModel: true },
  );

  const metrics = useMemo(() => {
    const row = chartData?.data?.[0];
    if (!row) {
      return null;
    }

    const newCustomers = Number(row[ApiMetric.METRIC_NEW_CUSTOMERS_COUNT] || 0);
    const repeatCustomers = Number(
      row[ApiMetric.METRIC_REPEAT_CUSTOMERS_COUNT] || 0,
    );
    const customers = Number(row[ApiMetric.METRIC_CUSTOMERS_COUNT] || 0);
    const sales = Number(row[ApiMetric.METRIC_SALES_COUNT] || 0);
    const revenue = Number(row[ApiMetric.METRIC_REVENUE] || 0);

    return {
      newCustomers,
      repeatCustomers,
      customers,
      newCustomersPct:
        customers > 0 ? percentageOf(newCustomers, customers) : 0,
      repeatPct: customers > 0 ? percentageOf(repeatCustomers, customers) : 0,
      avgOrdersPerCustomer: customers > 0 ? sales / customers : 0,
      avgCustomerValue: customers > 0 ? revenue / customers : 0,
    };
  }, [chartData]);

  return (
    <AnalyticsCard
      noToolbar
      ignoreAttrModel
      chart={insightsChart}
      sx={{ height: "100%", width: "100%" }}
      boxProps={{ px: 2, pt: 2.5, pb: 2, height: "100%" }}
      customHeader={
        <AnalyticsCardPanelHeader
          icon={<Groups fontSize="small" />}
          title="Customer Insights"
          statLabel={
            metrics
              ? `${formatMetric(ApiMetric.METRIC_CUSTOMERS_COUNT, metrics.customers)} customers`
              : undefined
          }
        />
      }
      body={
        loading ? (
          <Loading fullHeight />
        ) : (
          <Stack
            flex={1}
            justifyContent="center"
            spacing={1.25}
            height="100%"
            width="100%"
          >
            <InsightRow
              icon={<PersonAdd sx={{ fontSize: 20 }} />}
              label="New Customers"
              value={formatMetric(
                ApiMetric.METRIC_NEW_CUSTOMERS_COUNT,
                metrics?.newCustomers,
              )}
              subtitle={
                metrics
                  ? `${formatPercentage(metrics.newCustomersPct)} of total`
                  : undefined
              }
            />
            <InsightRow
              icon={<People sx={{ fontSize: 20 }} />}
              label="Repeat Customers"
              value={formatMetric(
                ApiMetric.METRIC_REPEAT_CUSTOMERS_COUNT,
                metrics?.repeatCustomers,
              )}
              subtitle={
                metrics
                  ? `${formatPercentage(metrics.repeatPct)} of total`
                  : undefined
              }
            />
            <InsightRow
              icon={<ShoppingCart sx={{ fontSize: 20 }} />}
              label="Average Orders per Customer"
              value={formatFloat(metrics?.avgOrdersPerCustomer)}
            />
            <InsightRow
              icon={<AttachMoney sx={{ fontSize: 20 }} />}
              label="Average Customer Value"
              value={formatMetric(
                ApiMetric.METRIC_REVENUE,
                metrics?.avgCustomerValue,
              )}
            />
          </Stack>
        )
      }
    />
  );
}

export default StoreCustomerInsights;
